import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Box, Snackbar, Alert } from "@mui/material";
import { Layout } from "../../components/layout";
import PageHeader from "../../components/PageHeader";
import PersonsInfo from "../../components/PersonsInfo";
import PageSection from "../../components/PageSection";
import StatusBadge from "../../components/StatusBadge";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
  AppointmentUpdateData,
  StepUpdateData,
} from "../../services/treatmentPlanService";
import {
  selectUser,
  selectIsProvider,
} from "../../store/selectors/authSelectors";
import { EditNoteIcon } from "../../components/Icons";
import {
  useModal,
  ApproveConfirmationModalContent,
  QRCodeSignModalContent,
} from "../../components/Modal";
import {
  TreatmentAppointment,
  AppointmentTimeSeparator,
  TreatmentPlanFooter,
  SummarizedView,
  SummarizedViewHandle,
} from "./components";
import { downloadTreatmentPlanPdf } from "../../utils/downloadTreatmentPlanPdf";
import { downloadSummarizedTreatmentPlanPdf } from "../../utils/downloadSummarizedTreatmentPlanPdf";
import { generateSummarizedContent } from "../../utils/generateSummarizedContent";
import {
  RejectionCommentBox,
  RejectionCommentTitle,
  RejectionCommentText,
  ViewToggleContainer,
  ViewToggleButton,
} from "./TreatmentPlanPage.styled";

type ActiveView = "detailed" | "summarized";

/** Converts a fetched TreatmentPlanResponse.appointments to the PUT body format */
function buildAppointmentUpdateData(
  treatmentPlan: TreatmentPlanResponse,
): AppointmentUpdateData[] {
  return [...treatmentPlan.appointments]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((appt) => ({
      id: appt.id,
      timeframeLabel: appt.timeframeLabel,
      conditionalPath: appt.conditionalPath || undefined,
      description: appt.description || undefined,
      sortOrder: appt.sortOrder,
      steps: [...appt.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(
          (step): StepUpdateData => ({
            id: step.id,
            name: step.name,
            description: step.description || undefined,
            sortOrder: step.sortOrder,
            durationMinutes: step.durationMinutes,
            fee: step.fee,
            codeSuggestions: step.codeSuggestions.map((cs) => ({
              id: cs.id,
              cdtCodeId: cs.cdtCodeId,
            })),
          }),
        ),
    }));
}

const TreatmentPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const user = useSelector(selectUser);

  const [treatmentPlan, setTreatmentPlan] =
    useState<TreatmentPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const [activeView, setActiveView] = useState<ActiveView>("detailed");
  const [isSummarizedEditing, setIsSummarizedEditing] = useState(false);

  const fetchInitiatedRef = useRef(false);
  const summarizedViewRef = useRef<SummarizedViewHandle>(null);

  const isProvider = useSelector(selectIsProvider);

  const fetchTreatmentPlan = useCallback(
    async (force = false) => {
      if (!id) return;
      if (!force && fetchInitiatedRef.current) return;
      fetchInitiatedRef.current = true;
      try {
        setLoading(true);
        setError(null);
        const data = await treatmentPlanService.getById(id);
        setTreatmentPlan(data);
      } catch (err) {
        setError(t("treatmentPlan.failedToLoad"));
        console.error("Error fetching treatment plan:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  useEffect(() => {
    fetchTreatmentPlan();
  }, [fetchTreatmentPlan]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const getStatusLabel = (status: number) => {
    switch (status) {
      case -1:
      case 0:
        return t("treatmentPlan.status.generating");
      case 1:
        return t("treatmentPlan.status.aiDrafted");
      case 2:
        return t("treatmentPlan.status.awaitingProviderReview");
      case 3:
        return t("treatmentPlan.status.awaitingPatientConfirmation");
      case 4:
        return t("treatmentPlan.status.declinedByPatient");
      case 5:
        return t("treatmentPlan.status.approvedByPatient");
      case 6:
        return t("treatmentPlan.status.inProgress");
      case 7:
        return t("treatmentPlan.status.completed");
      case 8:
        return t("treatmentPlan.status.generationFailed");
      default:
        return t("treatmentPlan.status.unknown");
    }
  };

  const getStatusType = (status: number) => {
    switch (status) {
      case -1:
      case 0:
      case 1:
      case 2:
        return "pending";
      case 3:
        return "in-progress";
      case 4:
        return "declined-by-patient";
      case 5:
      case 6:
        return "approved";
      case 7:
        return "completed";
      case 8:
        return "error";
      default:
        return "pending";
    }
  };

  const showSummarizedTab = treatmentPlan ? treatmentPlan.status >= 3 : false;

  const handleEditPlan = () => {
    if (activeView === "summarized") {
      summarizedViewRef.current?.startEdit();
    } else {
      navigate(`/treatment-plan/${id}/edit`);
    }
  };

  const handleDownloadDetailedPdf = async () => {
    if (!treatmentPlan) return;
    try {
      await downloadTreatmentPlanPdf({ treatmentPlan });
    } catch (err) {
      console.error("Error downloading PDF:", err);
    }
  };

  const handleDownloadSummarizedPdf = async () => {
    if (!treatmentPlan) return;
    const content =
      treatmentPlan.patientSummary?.trim() ||
      generateSummarizedContent(treatmentPlan);
    try {
      await downloadSummarizedTreatmentPlanPdf({ treatmentPlan, content });
    } catch (err) {
      console.error("Error downloading summarized PDF:", err);
    }
  };

  const handleSavePatientSummary = async (content: string) => {
    if (!treatmentPlan || !id) throw new Error("No treatment plan");
    const updated = await treatmentPlanService.update(id, {
      name: treatmentPlan.name,
      treatmentSummary: treatmentPlan.treatmentSummary,
      providerNote: treatmentPlan.providerNote,
      fee: treatmentPlan.fee || undefined,
      patientSummary: content,
      status: treatmentPlan.status,
      appointments: buildAppointmentUpdateData(treatmentPlan),
    });
    setTreatmentPlan(updated);
  };

  const handleEmailForReview = async () => {
    if (!id) return;
    try {
      await treatmentPlanService.shareByEmail(id);
      setEmailSuccess(true);
    } catch (err) {
      console.error("Error sending email for review:", err);
      setEmailError(true);
    }
  };

  const handleOpenToSign = () => {
    if (!id) return;
    openModal({
      id: "qr-code-sign",
      content: <QRCodeSignModalContent treatmentPlanId={id} />,
      showCloseButton: true,
    });
  };

  const handleApprove = () => {
    if (!user?.id || !id) return;
    openModal({
      id: "approve-confirmation-modal",
      title: "",
      content: (
        <ApproveConfirmationModalContent
          isLoading={isApproving}
          onCancel={() => closeModal("approve-confirmation-modal")}
          onApprove={async () => {
            try {
              setIsApproving(true);
              await treatmentPlanService.approve(id, user.id);
              closeModal("approve-confirmation-modal");
              fetchTreatmentPlan(true);
            } catch (err) {
              console.error("Error approving treatment plan:", err);
            } finally {
              setIsApproving(false);
            }
          }}
        />
      ),
      showCloseButton: true,
      maxWidth: "600px",
      containerPadding: "60px 90px",
      contentPadding: "0",
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Layout>
    );
  }

  if (error || !treatmentPlan) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <div>{error || t("treatmentPlan.notFound")}</div>
          <Button onClick={() => fetchTreatmentPlan(true)}>
            {t("treatmentPlan.retry")}
          </Button>
        </Box>
      </Layout>
    );
  }

  const sortedAppointments = [...treatmentPlan.appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const hasOptions = treatmentPlan.appointments.some(
    (appt) => appt.conditionalPath && appt.conditionalPath.trim() !== "",
  );

  const getPatientName = () => {
    if (treatmentPlan.patientFirstName || treatmentPlan.patientLastName) {
      return `${treatmentPlan.patientFirstName || ""} ${treatmentPlan.patientLastName || ""}`.trim();
    }
    return `Patient #${treatmentPlan.patientUid}`;
  };

  const getProviderName = () => {
    if (treatmentPlan.providerFirstName || treatmentPlan.providerLastName) {
      return `${treatmentPlan.providerFirstName || ""} ${treatmentPlan.providerLastName || ""}`.trim();
    }
    return `Provider #${treatmentPlan.providerId}`;
  };

  const metaItems = [
    { text: formatDate(treatmentPlan.dateGenerated), key: "date" },
    { text: formatTime(treatmentPlan.dateGenerated), key: "time" },
  ];

  if (treatmentPlan.status === 5 && treatmentPlan.approvedByPatientAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedByPatient", {
        name: getPatientName(),
        date: formatDate(treatmentPlan.approvedByPatientAt),
        time: formatTime(treatmentPlan.approvedByPatientAt),
      }),
      key: "approvedByPatient",
    });
  } else if (treatmentPlan.approvedByProviderAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedOn", {
        date: formatDate(treatmentPlan.approvedByProviderAt),
      }),
      key: "approved",
    });
  }

  const persons = [
    { name: getPatientName(), role: t("treatmentPlan.patient") },
    {
      name: getProviderName(),
      role: t("treatmentPlan.initiatedBy"),
      avatarUrl: treatmentPlan.providerAvatarUrl || undefined,
    },
  ];

  const summarizedContent =
    treatmentPlan.patientSummary?.trim() ||
    generateSummarizedContent(treatmentPlan);

  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {showSummarizedTab && (
        <ViewToggleContainer>
          <ViewToggleButton
            isActive={activeView === "detailed"}
            onClick={() => {
              setActiveView("detailed");
              setIsSummarizedEditing(false);
            }}
            disableRipple={activeView === "detailed"}
          >
            {t("treatmentPlan.detailedView")}
          </ViewToggleButton>
          <ViewToggleButton
            isActive={activeView === "summarized"}
            onClick={() => setActiveView("summarized")}
            disableRipple={activeView === "summarized"}
          >
            {t("treatmentPlan.summarizedView")}
          </ViewToggleButton>
        </ViewToggleContainer>
      )}

      {isProvider && treatmentPlan.status !== 7 && !isSummarizedEditing && (
        <Button variant="outlined" onClick={handleEditPlan}>
          <EditNoteIcon width="20px" height="20px" />
          {t("treatmentPlan.editPlan")}
        </Button>
      )}
    </Box>
  );

  return (
    <Layout>
      <PageHeader
        title={treatmentPlan.name}
        badge={
          <StatusBadge
            status={getStatusType(treatmentPlan.status)}
            label={getStatusLabel(treatmentPlan.status)}
          />
        }
        metaItems={metaItems}
        actions={headerActions}
      />

      {treatmentPlan.status === 4 && treatmentPlan.rejectionComment && (
        <RejectionCommentBox>
          <RejectionCommentTitle>
            {t("treatmentPlan.commentFromPatient")}
          </RejectionCommentTitle>
          <RejectionCommentText>
            {treatmentPlan.rejectionComment}
          </RejectionCommentText>
        </RejectionCommentBox>
      )}

      <PersonsInfo persons={persons} />

      {activeView === "detailed" && (
        <>
          <PageSection
            title={t("treatmentPlan.treatmentSummary")}
            content={treatmentPlan.treatmentSummary}
            allowHtml
          />

          {treatmentPlan.providerNote && (
            <PageSection
              title={t("treatmentPlan.noteFromProvider")}
              content={treatmentPlan.providerNote}
            />
          )}

          <PageSection title={t("treatmentPlan.treatmentSteps")}>
            {sortedAppointments.map((appointment, index) => (
              <React.Fragment key={appointment.id}>
                <TreatmentAppointment
                  appointment={appointment}
                  appointmentNumber={index + 1}
                />
                {index < sortedAppointments.length - 1 &&
                  appointment.timeframeLabel && (
                    <AppointmentTimeSeparator
                      timeframeLabel={appointment.timeframeLabel}
                    />
                  )}
              </React.Fragment>
            ))}
          </PageSection>
        </>
      )}

      {activeView === "summarized" && (
        <Box sx={{ mt: "24px" }}>
          <SummarizedView
            ref={summarizedViewRef as React.Ref<SummarizedViewHandle>}
            content={summarizedContent}
            onEditingChange={setIsSummarizedEditing}
            onSave={handleSavePatientSummary}
          />
        </Box>
      )}

      <TreatmentPlanFooter
        totalFee={treatmentPlan.totalFee}
        status={treatmentPlan.status}
        isProvider={isProvider}
        hasMultipleOptions={hasOptions}
        isSummarizedView={activeView === "summarized"}
        onApprove={handleApprove}
        onDownloadPdf={handleDownloadDetailedPdf}
        onDownloadSummarizedPdf={handleDownloadSummarizedPdf}
        onEmailForReview={handleEmailForReview}
        onOpenToSign={handleOpenToSign}
      />

      <Snackbar
        open={emailError}
        autoHideDuration={6000}
        onClose={() => setEmailError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("treatmentPlan.emailSendFailed")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={emailSuccess}
        autoHideDuration={6000}
        onClose={() => setEmailSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("treatmentPlan.emailSentSuccess")}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TreatmentPlanPage;
